// Landlord-side /manage/messages — thread inbox
import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Loader2, MessageSquare, Mail } from "lucide-react";
import { toast } from "sonner";

export default function ManageMessages() {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/manage/messages");
      setThreads(data);
      if (data.length > 0 && !selected) setSelected(data[0]);
      else if (selected) setSelected(data.find((t) => t.id === selected.id) || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const send = async () => {
    if (!selected || !reply.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/manage/messages/${selected.id}/messages`, { content: reply });
      setSelected(data);
      setReply("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="manage-messages-page">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Inbox</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Tenant messages</h1>
          <p className="mt-1 text-sm text-slate-500">Reply to tenants directly. No AI involvement — your words.</p>
        </header>

        {loading ? (
          <div className="flex h-64 items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : threads.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="manage-messages-empty">
            No tenant messages yet. Threads appear here when a tenant uses "Message landlord" from their portal.
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-[280px,1fr]">
            <aside className="rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
              <ul className="space-y-1" data-testid="message-threads-list">
                {threads.map((t) => (
                  <li key={t.id}>
                    <button
                      onClick={() => setSelected(t)}
                      data-testid={`thread-${t.id}`}
                      className={`w-full rounded-md p-3 text-left transition ${selected?.id === t.id ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                    >
                      <p className="text-sm font-semibold text-slate-900">{t.tenant?.name || "Tenant"}</p>
                      {t.last_message && (
                        <p className="mt-0.5 truncate text-xs text-slate-500">
                          {t.last_message.role === "landlord" ? "You: " : ""}{t.last_message.content}
                        </p>
                      )}
                      <p className="mt-1 text-[10px] text-slate-400">{new Date(t.updated_at).toLocaleString()} · {t.message_count} msg{t.message_count !== 1 ? "s" : ""}</p>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              {!selected ? (
                <p className="text-sm text-slate-500">Select a thread.</p>
              ) : (
                <>
                  <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{selected.tenant?.name}</p>
                      <p className="text-xs text-slate-500">{selected.tenant?.email}</p>
                    </div>
                    <MessageSquare className="h-4 w-4 text-slate-300" />
                  </div>
                  <ol className="max-h-96 space-y-3 overflow-y-auto" data-testid="thread-messages">
                    {(selected.messages || []).map((m, i) => (
                      <li key={`${m.role}-${m.created_at}-${i}`} className={`flex ${m.role === "landlord" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "landlord" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                          <p>{m.content}</p>
                          <p className={`mt-0.5 text-[9px] ${m.role === "landlord" ? "text-indigo-200" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-4 flex gap-2">
                    <Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" data-testid="landlord-msg-input" />
                    <Button onClick={send} className="self-end bg-indigo-600 text-white hover:bg-indigo-700" disabled={sending} data-testid="landlord-msg-send">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </AppShell>
  );
}
